import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { DailyTask, TaskSource } from '../../lib/dailyApi';
import type { PersonTask } from '../../lib/peopleApi';
import type { RelatedTaskSummary, Task, TaskPriority } from '../../lib/tasksApi';
import type { TaskStatusCategory } from '../../lib/taskStatusesApi';
import type { WeeklyDayChange, WeeklyTask } from '../../lib/weeklyApi';
import { formatEstimate } from '../../lib/estimate';
import { shortIdentifier } from '../../lib/taskIdentifier';
import { taskDetailPath } from '../../utils/taskGrouping';
import { Avatar } from './linear/Avatar';
import { DUE_COLOR, daysUntil, dueUrgency, formatDueLabel } from './linear/dueDate';
import { PriorityIcon, StatusIcon } from './linear/taskVisuals';

/**
 * The one task row used everywhere a summary lists tasks — the dashboard's
 * "Task-uri & urgențe", the Daily Summary buckets and the Weekly Summary
 * cards. Linear's anatomy (CLE-174): priority icon → mono identifier → status
 * icon → title → project chip → due info → assignee avatar.
 *
 * It takes a flat, presentation-only shape rather than any one API type, so
 * the dashboard's `DashboardUrgentTask`, the daily `DailyTask` and the weekly
 * `WeeklyTask` all feed it through the small adapters below.
 */
export interface TaskListRowData {
  id: string;
  identifier: string | null;
  title: string;
  projectName: string | null;
  /** Assignee display name; null renders the "unassigned" glyph. */
  assignee: string | null;
  priority: TaskPriority;
  statusLabel: string;
  statusColor: string;
  statusCategory: TaskStatusCategory;
  dueDate: string | null;
  /** Precomputed by the dashboard; derived from `dueDate` when absent. */
  daysOverdue?: number;
  /** 'personal' adds the amber "Personal" chip the summaries use. */
  source?: TaskSource;
  /** Decimal hours; shown as "1h 30m" before the avatar when set (the calendar). */
  estimate?: number | null;
}

export type TaskListRowTone = 'overdue' | 'critical' | 'blocked' | 'plain';

interface Props {
  task: TaskListRowData;
  /** Overdue rows show "N zile" in red instead of the due label. */
  tone?: TaskListRowTone;
  /** The summaries open tasks in a new tab so the report stays on screen. */
  newTab?: boolean;
  /**
   * Open the task in place (a modal) instead of navigating — the task detail
   * page's own sub-issue and relation lists. Renders a button, not a link.
   */
  onOpen?: () => void;
  /**
   * Narrow columns (the chat profile panel): drop the identifier and project
   * chip, which the viewport breakpoints would otherwise keep on a desktop.
   */
  compact?: boolean;
  /**
   * Drop the assignee avatar — for lists that sit under the member's own
   * heading (the weekly by-day timeline), where it only repeats the heading.
   */
  hideAssignee?: boolean;
  /** Something after the avatar, e.g. a remove button. */
  trailing?: ReactNode;
  /** An optional second line under the row (the weekly transition timeline). */
  children?: ReactNode;
}

/** Due info slot: overdue rows show "N zile", the rest the Linear due label. */
function DueInfo({ task, tone }: { task: TaskListRowData; tone: TaskListRowTone }) {
  // A finished task is not "overdue" — the weekly report lists done tasks
  // whose due date has long passed, and painting those red would report a
  // problem that does not exist.
  const settled = task.statusCategory === 'COMPLETED' || task.statusCategory === 'CANCELLED';
  if (settled) {
    if (!task.dueDate) return null;
    return (
      <span className="ml-2 hidden shrink-0 text-[12px] text-gray-400 sm:inline">
        {formatDueLabel(task.dueDate)}
      </span>
    );
  }
  if (tone === 'overdue') {
    const days =
      task.daysOverdue ?? (task.dueDate ? Math.max(0, -daysUntil(new Date(task.dueDate))) : 0);
    if (days > 0) {
      return (
        <span
          className="ml-2 hidden shrink-0 text-[12px] font-medium sm:inline"
          style={{ color: DUE_COLOR.overdue }}
        >
          {days} {days === 1 ? 'zi' : 'zile'}
        </span>
      );
    }
  }
  const urgency = dueUrgency(task.dueDate);
  if (urgency === 'none') return null;
  return (
    <span
      className="ml-2 hidden shrink-0 text-[12px] sm:inline"
      style={{ color: DUE_COLOR[urgency] }}
    >
      {formatDueLabel(task.dueDate)}
    </span>
  );
}

export function TaskListRow({
  task,
  tone = 'plain',
  newTab = false,
  onOpen,
  compact = false,
  hideAssignee = false,
  trailing,
  children,
}: Props) {
  const body = (
    <>
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-gray-400">
        <PriorityIcon priority={task.priority} size={16} />
      </span>
      {!compact && (
        <span
          title={task.identifier ?? undefined}
          className="ml-1.5 hidden shrink-0 font-mono text-[13px] tabular-nums text-gray-400 sm:inline"
        >
          {task.identifier ? shortIdentifier(task.identifier) : '—'}
        </span>
      )}
      <span className="ml-2 flex shrink-0 items-center" title={task.statusLabel}>
        <StatusIcon category={task.statusCategory} color={task.statusColor} />
      </span>
      <span className="ml-1.5 min-w-0 flex-1 truncate text-[13px] leading-[1.4] text-gray-800">
        {task.title}
      </span>
      {task.source === 'personal' && (
        <span className="ml-3 hidden shrink-0 rounded-full border border-amber-300 px-2 py-px text-[11px] font-medium text-amber-700 md:inline-block">
          Personal
        </span>
      )}
      {!compact && task.projectName && (
        <span
          title={task.projectName}
          className="ml-2 hidden max-w-[110px] shrink-0 truncate rounded-full border border-gray-300 px-1.5 py-px text-[10.5px] font-medium text-gray-500 md:inline-block"
        >
          {task.projectName}
        </span>
      )}
      <DueInfo task={task} tone={tone} />
      {task.estimate != null && (
        <span className="ml-2 shrink-0 text-[12px] tabular-nums text-gray-400">
          {formatEstimate(task.estimate)}
        </span>
      )}
      {!hideAssignee && (
        <span className="ml-1.5 flex shrink-0 items-center">
          <Avatar name={task.assignee} size={20} />
        </span>
      )}
    </>
  );
  const path = onOpen ? null : taskDetailPath(task);
  // With a trailing slot the <li> is flex: the row must take the remaining
  // width (and be allowed to shrink) or its content hugs and spills out of a
  // narrow card — the "all users" daily cards showed chips cut off at the edge.
  const rowClass = `flex h-9 items-center pl-2 pr-3 ${trailing ? 'min-w-0 flex-1' : ''}`;
  const interactive = `${rowClass} transition-colors duration-75 hover:bg-gray-200`;
  return (
    <li className={trailing ? 'flex min-w-0 items-center' : undefined}>
      {onOpen ? (
        <button type="button" onClick={onOpen} className={`${interactive} w-full text-left`}>
          {body}
        </button>
      ) : path ? (
        <Link
          to={path}
          target={newTab ? '_blank' : undefined}
          rel={newTab ? 'noopener noreferrer' : undefined}
          className={`${rowClass} transition-colors duration-75 hover:bg-gray-200`}
        >
          {body}
        </Link>
      ) : (
        <div className={rowClass}>{body}</div>
      )}
      {trailing}
      {children && <div className="-mt-1 pb-1.5 pl-9 pr-3">{children}</div>}
    </li>
  );
}

// --- Adapters from the API shapes -----------------------------------------

/** The full task list shape (tasks page, sub-issues, client project cards). */
export function taskRow(task: Task): TaskListRowData {
  return {
    id: task.id,
    identifier: task.identifier,
    title: task.title,
    projectName: task.projectName,
    assignee: task.assignee?.name ?? null,
    priority: task.priority,
    statusLabel: task.statusLabel,
    statusColor: task.statusColor,
    statusCategory: task.statusCategory,
    dueDate: task.dueDate,
  };
}

/**
 * The calendar's phone agendas: the task row plus its estimate, since the day
 * header sums estimates and the row should show what it contributes.
 */
export function calendarTaskRow(task: Task): TaskListRowData {
  return { ...taskRow(task), estimate: task.estimate };
}

/** A person's workload row; the person is implied, so the name is passed in. */
export function personTaskRow(task: PersonTask, assignee: string | null): TaskListRowData {
  return {
    id: task.id,
    identifier: task.identifier,
    title: task.title,
    projectName: task.projectName,
    assignee,
    priority: task.priority,
    statusLabel: task.statusLabel,
    statusColor: task.statusColor,
    statusCategory: task.statusCategory,
    dueDate: task.dueDate,
  };
}

export function relatedTaskRow(task: RelatedTaskSummary): TaskListRowData {
  return {
    id: task.id,
    identifier: task.identifier,
    title: task.title,
    projectName: task.projectName,
    assignee: task.assignee,
    priority: task.priority,
    statusLabel: task.statusLabel,
    statusColor: task.statusColor,
    statusCategory: task.statusCategory,
    dueDate: task.dueDate,
  };
}

export function dailyTaskRow(task: DailyTask): TaskListRowData {
  return {
    id: task.id,
    identifier: task.identifier,
    title: task.title,
    projectName: task.projectName,
    assignee: task.assignee?.name ?? null,
    priority: task.priorityLevel,
    statusLabel: task.statusLabel,
    statusColor: task.statusColor,
    statusCategory: task.statusCategory,
    dueDate: task.dueDate,
    source: task.source,
  };
}

export function weeklyTaskRow(task: WeeklyTask): TaskListRowData {
  return {
    id: task.id,
    identifier: task.identifier,
    title: task.title,
    projectName: task.projectName,
    assignee: task.assignee?.name ?? null,
    priority: task.priorityLevel,
    statusLabel: task.statusLabel,
    statusColor: task.statusColor,
    statusCategory: task.statusCategory,
    dueDate: task.dueDate,
    source: task.source,
  };
}

/** The by-day timeline knows the member from its heading, so it passes the name in. */
export function weeklyChangeRow(change: WeeklyDayChange, assignee: string): TaskListRowData {
  const { task } = change;
  return {
    id: task.id,
    identifier: task.identifier,
    title: task.title,
    projectName: task.projectName,
    assignee,
    priority: task.priorityLevel,
    statusLabel: task.statusLabel,
    statusColor: task.statusColor,
    statusCategory: task.statusCategory,
    dueDate: task.dueDate,
    source: task.source,
  };
}
